import {
  Autocomplete,
  Box,
  Chip,
  FormHelperText,
  FormLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { CloseIcon } from 'assets/icons';
import { REGEX } from 'constant/regex';
import { FC, useEffect, useState } from 'react';

interface AppAutoCompleteProps {
  id?: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  limitTags?: number;
  options?: Option[];
  valueType?: 'string' | 'array';
  value?: string[] | string;
  defaultValue?: string[] | string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: any;
}

const getOptionLabel = (option: string | Option) => {
  return typeof option === 'string' ? option : option.label;
};

const TagInput: FC<AppAutoCompleteProps> = ({
  id,
  required,
  label,
  limitTags,
  options = [],
  placeholder,
  valueType = 'string',
  value,
  defaultValue,
  onChange,
  error,
  disabled,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [values, setValues] = useState<string[]>([]);

  useEffect(() => {
    if (defaultValue) {
      setValues(
        typeof defaultValue === 'string'
          ? defaultValue.split(',')
          : defaultValue
      );
    }
  }, []);

  useEffect(() => {
    if (value) {
      setValues(typeof value === 'string' ? value.split(',') : value);
    }
  }, [value]);

  const addTag = (newValue: string) => {
    if (newValue) {
      const oldValueIdx = values.findIndex((tag) => tag === newValue);
      if (oldValueIdx > -1) {
        values.splice(oldValueIdx, 1);
      }

      const newValues = values.concat(newValue);
      setValues(newValues);
      onChange?.(valueType === 'string' ? newValues.join(',') : newValues);
    }

    setInputValue('');
  };

  const handleChange = (
    _event: React.SyntheticEvent | null,
    newValue: (string | Option)[]
  ) => {
    const newValues = newValue
      .map((value) => {
        if (typeof value === 'object') {
          if (!newValue.includes(value.label)) return getOptionLabel(value);
          return '';
        } else {
          return getOptionLabel(value);
        }
      })
      .filter((item) => item !== '');
    setValues(newValues);
    onChange?.(valueType === 'string' ? newValues.join(',') : newValues);
  };

  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (['Enter', ' ', ','].includes(event.key)) {
      const target = event.target as HTMLInputElement;
      addTag(target.value);
      event.preventDefault();
    } else if (REGEX.SPECIAL.test(event.key)) {
      event.preventDefault();
    }
  };

  const handleDelete = (option: string | Option) => {
    const deleteValue = getOptionLabel(option);
    const newValues = values.filter((ele) => ele !== deleteValue);
    setValues(newValues);
    onChange?.(valueType === 'string' ? newValues.join(',') : newValues);
  };

  const handleBlur = () => {
    addTag(inputValue);
  };

  return (
    <Grid
      container
      direction={'column'}
      sx={{ gap: { xs: '4px', sm: '10px' } }}
    >
      {label && (
        <Grid item xs={4}>
          <FormLabel
            htmlFor={id}
            required={required}
            sx={{ color: '#000000de' }}
          >
            {label}
          </FormLabel>
        </Grid>
      )}
      <Grid
        item
        xs={8}
        sx={{
          width: '100%',
          height: 'max-content',
          '& .MuiInputBase-input': {
            py: '4.63px !important',
          },
        }}
      >
        <Autocomplete
          id={id}
          multiple
          freeSolo
          size='small'
          limitTags={limitTags}
          getOptionLabel={getOptionLabel}
          disabled={disabled}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  label={getOptionLabel(option)}
                  {...tagProps}
                  color='primary'
                  deleteIcon={<CloseIcon fontSize='small' />}
                  onDelete={() => handleDelete(option)}
                />
              );
            })
          }
          inputValue={inputValue}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={!value ? placeholder : ''}
              onChange={handleInput}
              onKeyDown={handleKeyDown}
            />
          )}
          options={options}
          renderOption={(props, option) => (
            <Box
              component='li'
              {...props}
              key={typeof option === 'string' ? option : option.value}
            >
              <Typography
                component='span'
                sx={{
                  fontSize: '18px !important',
                }}
              >
                {typeof option === 'string' ? option : option.label}
              </Typography>
            </Box>
          )}
          value={values}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        {error && <FormHelperText error>{error}</FormHelperText>}
      </Grid>
    </Grid>
  );
};

export default TagInput;
